import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { scrollToId } from "@/lib/site";
import { useLang } from "@/lib/i18n";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { t, lang, setLang, logo } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#0B0C10]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToId("top")}
          className="flex items-center gap-3 group"
        >
          <img src={logo} alt="Code Echo First Aid" className="h-8 md:h-10 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {t.nav_links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          {/* Language toggle */}
          <div
            data-testid="lang-toggle"
            className="flex items-center rounded-sm border border-white/15 overflow-hidden text-[11px] font-bold uppercase tracking-[0.14em]"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1.5 transition-colors duration-200 ${
                lang === "en" ? "bg-[#0057B7] text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-2.5 py-1.5 transition-colors duration-200 ${
                lang === "fr" ? "bg-[#E11B22] text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              FR
            </button>
          </div>

          <button
            data-testid="nav-cta"
            onClick={() => scrollToId("form")}
            className="group relative overflow-hidden rounded-sm border border-white/15 px-4 md:px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:border-[#E11B22]"
          >
            <span className="relative z-10">{t.nav_cta}</span>
            <span className="absolute inset-0 -translate-x-full bg-[#E11B22] transition-transform duration-300 group-hover:translate-x-0" />
          </button>
        </div>
      </div>
    </motion.header>
  );
}
